<template>
  <ClientOnly>
    <LiteYouTubeEmbed
      :id="id"
      ref="iframe"
      :title="title"
      @iframe-added="onIframeLoad"
    />
  </ClientOnly>
</template>

<script setup lang="ts">
import LiteYouTubeEmbed from 'vue-lite-youtube-embed'
import 'vue-lite-youtube-embed/style.css'
import type { YouTubePlayer, YouTubePlayerEvent } from '~/utils/youtube'

class YouTubeAPINotLoadedError extends Error {
  constructor() {
    super('YouTube Player API not loaded yet')
    this.name = 'YouTubeAPINotLoadedError'
  }
}

type LiteYouTubeEmbedRef = {
  getPlayerInstance: () => HTMLElement
  playVideo: () => void
  warmConnections: () => void
  addIframe: () => void
}


const iframe = ref<LiteYouTubeEmbedRef | null>(null)
const playerInstance = ref<YouTubePlayer>(null)
const playerCheckInterval = ref<number | null>(null)
const title = ref('')

const props = defineProps<{
  blok?: {
    youtube: string
  }
  url?: string
  autoPlay?: boolean
}>()

const emit = defineEmits(['onVideoEnded'])
const url = computed(() => props.blok?.youtube || props.url)
const id = computed(() => getYouTubeIdFromURL(url.value) as string)

function onIframeLoad() {
  try {
    initPlayerInstance()
  } catch (e) {
    if (e instanceof YouTubeAPINotLoadedError) {
      console.debug('YouTube player script not loaded yet, injecting script...')
      doInjectPlayerScript()
    } else {
      console.error('Unexpected error initializing YouTube player:', e)
    }
  }
}

function onStateChange(event: YouTubePlayerEvent) {
  if (event.data === YouTubePlayerState.ENDED) {
    emit('onVideoEnded')
  }
}

const initPlayerInstance = () => {
  if (!window.YT?.Player) {
    throw new YouTubeAPINotLoadedError()
  }
  const iframeElement = iframe.value
  if (!iframeElement) {
    throw new Error('Iframe element not found')
  }
  playerInstance.value = new window.YT.Player(
    iframeElement.getPlayerInstance(),
    {
      events: {
        onStateChange,
      },
    },
  )
  if (props.autoPlay) {
    iframe.value.playVideo()
  }
}

const doInjectPlayerScript = () => {
  const script = document.createElement('script')
  script.setAttribute('src', 'https://www.youtube.com/player_api')

  script.onload = () => {
    playerCheckInterval.value = window.setInterval(() => {
      checkPlayer()
    }, 32)
  }

  document.head.appendChild(script)
}

const checkPlayer = () => {
  if (window.YT?.Player) {
    if (playerCheckInterval.value) {
      clearInterval(playerCheckInterval.value)
    }
    try {
      initPlayerInstance()
      return true
    } catch (e) {
      console.error('Unexpected error initializing YouTube player:', e)
      return false
    }
  }
  return false
}

watch(iframe, () => {
  if (props.autoPlay && iframe.value) {
    iframe.value.warmConnections()
    iframe.value.addIframe()
  }
})

onBeforeUnmount(() => {
  if (playerCheckInterval.value) {
    clearInterval(playerCheckInterval.value)
  }
  playerInstance.value = null
})
</script>

<style lang="less" scoped>
.media.video {
  .media__container {
    .image-aspect-16-9();

    .player {
      > div {
        .absolute-fill();
      }
    }
  }
}
</style>
